import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../app/hooks";
import { resetMaterialUpload } from "./materials-slice";
import { MaterialAudience } from "../../components/materials/MaterialAudiencePicker";
import { Material } from "./models/Material";
import {
  deleteMaterial,
  updateMaterial,
  uploadMaterial,
} from "./slice-extensions/materials-slice-extension";

/**
 * Materials-page controller: holds the pending-upload and edit dialog state and
 * runs the upload / edit / delete actions with their toasts.
 */
export const useMaterialActions = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [pendingFile, setPendingFileState] = useState<File | null>(null);

  /** Picking or closing a file starts the upload dialog afresh (no stale failure or progress). */
  const setPendingFile = (file: File | null) => {
    dispatch(resetMaterialUpload());
    setPendingFileState(file);
  };
  const [editing, setEditing] = useState<Material | null>(null);
  const [busy, setBusy] = useState(false);

  const submitUpload = async (
    category: string,
    description: string,
    notifyMembers: boolean,
    audience: MaterialAudience,
  ) => {
    if (!pendingFile) return;
    const result = await dispatch(
      uploadMaterial({
        file: pendingFile,
        category: category || undefined,
        description: description || undefined,
        notifyMembers,
        memberIds: audience.everyone ? undefined : audience.memberIds,
        groupIds: audience.everyone ? undefined : audience.groupIds,
      }),
    );
    if (uploadMaterial.fulfilled.match(result)) {
      toast.success(
        notifyMembers
          ? t("materials.toastUploadedNotified", "File uploaded — members were notified.")
          : t("materials.toastUploaded", "File uploaded."),
      );
      setPendingFile(null);
    }
    // A failure stays in the dialog as its own line (materials.upload.failed), so the file can be sent again.
  };

  const submitEdit = async (category: string, description: string, audience: MaterialAudience) => {
    if (!editing) return;
    setBusy(true);
    const result = await dispatch(
      updateMaterial({
        id: editing.id,
        request: {
          category,
          description,
          memberIds: audience.everyone ? [] : audience.memberIds,
          groupIds: audience.everyone ? [] : audience.groupIds,
        },
      }),
    );
    setBusy(false);
    if (updateMaterial.fulfilled.match(result)) {
      setEditing(null);
    } else {
      toast.error(result.payload?.message ?? t("materials.saveFailed", "Failed to save the material."));
    }
  };

  const remove = async (material: Material) => {
    const result = await dispatch(deleteMaterial(material.id));
    if (deleteMaterial.fulfilled.match(result)) {
      toast.success(t("materials.toastDeleted", "Material deleted."));
    } else {
      toast.error(result.payload?.message ?? t("materials.deleteFailed", "Failed to delete the material."));
    }
  };

  return { pendingFile, setPendingFile, editing, setEditing, busy, submitUpload, submitEdit, remove };
};
